import React, { useEffect } from 'react';
import { X, Printer } from 'lucide-react';
import { PaymentReceiptData, ExpenseReceiptData } from '../../api/documents';
import { PrintablePaymentReceipt } from './PrintablePaymentReceipt';
import { PrintableExpenseReceipt } from './PrintableExpenseReceipt';

interface ReceiptPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'payment' | 'expense';
  data: PaymentReceiptData | ExpenseReceiptData;
}

/**
 * Modal for previewing and printing receipts
 * Uses browser's native print dialog via window.print()
 */
export const ReceiptPrintModal: React.FC<ReceiptPrintModalProps> = ({
  isOpen,
  onClose,
  type,
  data,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    // Add print styles when modal is open
    const styleId = 'receipt-print-styles';

    // Remove existing style if present to prevent duplicates
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Inject new style tag
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media print {
        @page {
          size: A5;
          margin: 10mm;
        }

        /* Hide everything except the receipt content */
        body * {
          visibility: hidden;
        }

        /* Show only the receipt content */
        .receipt-content,
        .receipt-content * {
          visibility: visible;
        }

        /* Position receipt content at the top of the page */
        .receipt-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          background: white;
        }

        /* Ensure flex layout works */
        .receipt-content .flex {
          display: flex !important;
        }

        .receipt-content .inline-block {
          display: inline-block !important;
        }

        /* Ensure text alignment works */
        .receipt-content .text-center {
          text-align: center !important;
        }

        .receipt-content .text-right {
          text-align: right !important;
        }

        /* Ensure background colors print */
        .receipt-content .bg-gray-100 {
          background-color: #f5f5f5 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .receipt-content .bg-gray-50 {
          background-color: #fafafa !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Ensure text colors print */
        .receipt-content .text-red-600 {
          color: #dc2626 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .receipt-content .text-gray-600 {
          color: #4b5563 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .receipt-content .text-gray-700 {
          color: #374151 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Ensure borders print */
        .receipt-content .border-gray-800 {
          border-color: #1f2937 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .receipt-content .border-l-4 {
          border-left-width: 4px !important;
        }

        .receipt-content .border-t {
          border-top-width: 1px !important;
        }

        /* Reset any modal styling */
        .receipt-content .rounded {
          border-radius: 0 !important;
        }
      }
    `;
    document.head.appendChild(style);

    // Cleanup on unmount - always remove the style tag
    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4"
      onClick={handleBackgroundClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center z-10">
          <h2 className="text-lg font-semibold">
            {type === 'payment' ? 'Phiếu Thu Preview' : 'Phiếu Chi Preview'}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Receipt Content */}
        <div className="p-4">
          {type === 'payment' ? (
            <PrintablePaymentReceipt data={data as PaymentReceiptData} />
          ) : (
            <PrintableExpenseReceipt data={data as ExpenseReceiptData} />
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
};
